import { useMyOrgFeaturesQuery } from "@/graphql"
import { FC, ReactNode, useCallback, useEffect } from "react"
import { useAuth } from "./AuthProvider"
import { FeatureContext } from "./FeatureContext"

type Props = {
  children: ReactNode
}

/**
 * FeatureProvider — fetches the authenticated user's organization feature
 * entitlements and makes them available via FeatureContext / useFeature().
 *
 * Mounted inside AuthProvider so it can read `user` from auth state.
 * Deliberately does NOT touch AuthProvider itself.
 *
 * Fail-closed: while loading, every boolean feature returns `false`.
 * Features are re-fetched on window focus and whenever a caller invokes
 * refetchFeatures() (e.g. after a toggle, or on entering Create App).
 */
const FeatureProvider: FC<Props> = ({ children }) => {
  const { user } = useAuth()

  const { data, loading, refetch } = useMyOrgFeaturesQuery({
    skip: !user,
    fetchPolicy: "cache-and-network",
  })

  const refetchFeatures = useCallback(() => {
    if (!user) return Promise.resolve()
    return refetch()
  }, [user, refetch])

  useEffect(() => {
    const onFocus = () => {
      if (user && document.visibilityState === "visible") refetch()
    }
    window.addEventListener("focus", onFocus)
    document.addEventListener("visibilitychange", onFocus)
    return () => {
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("visibilitychange", onFocus)
    }
  }, [user, refetch])

  const features = (data?.myOrgFeatures ?? null) as Record<string, boolean | number | string> | null

  return (
    <FeatureContext.Provider value={{ features, isFeaturesLoading: loading, refetchFeatures }}>
      {children}
    </FeatureContext.Provider>
  )
}

export default FeatureProvider
